import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import {
  fetchPermissionsTree,
  fetchRolePermissions,
  updateRolePermissions,
} from "redux/actions/RolesAndPermissionsAction";
import { toast } from "react-toastify";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const extractIds = (node) => {
  let ids = [...(node.permissions?.map((p) => p.id) || [])];
  if (node.subModules)
    node.subModules.forEach((sub) => {
      ids = [...ids, ...extractIds(sub)];
    });
  return ids;
};

// ─── Recursive Permission Accordion Node ────────────────────────────────────

const PermissionNode = ({
  node,
  checkedIds,
  onToggle,
  expanded,
  onExpandToggle,
}) => {
  const allIds = extractIds(node);
  const isChecked =
    allIds.length > 0 && allIds.every((id) => checkedIds.includes(id));
  const isIndeterminate =
    !isChecked && allIds.some((id) => checkedIds.includes(id));

  // Tracks which single sub-module (child of this node) is currently open,
  // so that expanding one sibling collapses the others — same exclusive
  // behavior as the top-level accordion group.
  const [expandedSubId, setExpandedSubId] = useState(null);

  const handleParentToggle = (e) => {
    e.stopPropagation();
    if (isChecked) {
      onToggle(checkedIds.filter((id) => !allIds.includes(id)));
    } else {
      onToggle([...new Set([...checkedIds, ...allIds])]);
    }
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={() => onExpandToggle(node.id)}
      disableGutters
      square
      sx={{
        mb: 1,
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        "&:before": { display: "none" },
        boxShadow: "none",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          bgcolor: "#f5f5f5",
          borderRadius: 1,
          minHeight: 0,
          "& .MuiAccordionSummary-content": {
            alignItems: "center",
            my: 0.5,
          },
        }}
      >
        <Checkbox
          checked={isChecked}
          indeterminate={isIndeterminate}
          onClick={handleParentToggle}
          size="small"
        />
        <Typography variant="body2" fontWeight="700">
          {node.name}
          {node.businessType && (
            <Typography
              component="span"
              variant="body2"
              fontWeight="700"
              className="colorPrimary"
              sx={{ ml: 0.5 }}
            >
              ({node.businessType})
            </Typography>
          )}
        </Typography>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 1 }}>
        {node.permissions?.map((p) => (
          <Box
            key={p.id}
            sx={{ display: "flex", alignItems: "center", py: 0.5, ml: 2 }}
          >
            <Checkbox
              checked={checkedIds.includes(p.id)}
              onChange={() =>
                onToggle(
                  checkedIds.includes(p.id)
                    ? checkedIds.filter((id) => id !== p.id)
                    : [...checkedIds, p.id],
                )
              }
              size="small"
            />
            <Typography variant="body2">{p.name}</Typography>
          </Box>
        ))}

        {node.subModules?.map((sub) => (
          <Box key={sub.id} sx={{ ml: 3 }}>
            <PermissionNode
              node={sub}
              checkedIds={checkedIds}
              onToggle={onToggle}
              expanded={expandedSubId === sub.id}
              onExpandToggle={(id) =>
                setExpandedSubId((prev) => (prev === id ? null : id))
              }
            />
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

// ─── Main Modal ──────────────────────────────────────────────────────────────

export default function ParentPermissionsModal({ open, onClose, parentRole }) {
  const dispatch = useDispatch();
  const { permissionsTree, loading } = useSelector(
    (state) => state.rbacReducer,
  );

  const [checkedIds, setCheckedIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [expandedModuleId, setExpandedModuleId] = useState(null);

  useEffect(() => {
    if (open && parentRole?.id) {
      setCheckedIds([]);
      setExpandedModuleId(null);
      dispatch(fetchPermissionsTree());
      dispatch(fetchRolePermissions(parentRole.id)).then((res) => {
        if (res?.success) setCheckedIds(res.data?.assignedPermissionIds || []);
      });
    }
  }, [open, parentRole?.id]);

  const handleSave = () => {
    setSaving(true);
    dispatch(updateRolePermissions(parentRole.id, checkedIds)).then((res) => {
      setSaving(false);
      if (res?.success) {
        toast.success("Parent role permissions updated successfully!");
        onClose();
      }
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            Parent Role Permissions
          </Typography>
          {parentRole && (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
              <Chip
                label={`Role: ${parentRole.name}`}
                size="small"
                variant="outlined"
                color="primary"
              />
              <Chip
                label={`Platform: ${parentRole.platform}`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`Business Type: ${parentRole.businessType}`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`Role Type: ${parentRole.roleType}`}
                size="small"
                variant="outlined"
              />
            </Box>
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ minHeight: 300, pt: 2 }}>
        {loading && !permissionsTree.length ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : permissionsTree.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" py={4}>
            No permissions found.
          </Typography>
        ) : (
          permissionsTree.map((mod) => (
            <PermissionNode
              key={mod.id}
              node={mod}
              checkedIds={checkedIds}
              onToggle={setCheckedIds}
              expanded={expandedModuleId === mod.id}
              onExpandToggle={(id) =>
                setExpandedModuleId((prev) => (prev === id ? null : id))
              }
            />
          ))
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading}
        >
          {saving ? "Saving..." : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
